/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import styled from "styled-components/macro";

import API from "Signups/SignupView/API";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state";

import FnrModal from "UI/FnrModal";

import { Loading } from "./Components";
import ErrorMessage from "./ErrorMessage";
import NoRefund from "./NoRefund";
import RemoveWithRefund from "./RemoveWithRefund";
import { personType } from "./personType";

const getDisplayName = ({ user, household, additionalName }) => {
  if (user) {
    const name = user.name
      ? user.name
      : `${user.fname}${user.lname ? ` ${user.lname}` : ""}`;
    return name;
  }

  if (household) {
    return household.householdTitle;
  }

  if (additionalName) {
    return `${additionalName.fname}${
      additionalName.lname ? ` ${additionalName.lname}` : ""
    }`;
  }
};

const getPostDataForRefundInfo = ({
  removalType,
  signupListItemID,
  userID,
  householdID,
  additionalNameID,
  isItemType,
  updatedNumberOfSlots,
}) => {
  if (isItemType && removalType === "slot") {
    return {
      signupListItemID,
      userID,
      numberOfSlots: updatedNumberOfSlots,
    };
  }
  if (removalType === personType.HOUSEHOLD_MEMBER) {
    return {
      signupListItemID,
      userID,
    };
  }
  if (removalType === personType.USER) {
    return {
      signupListItemID,
      userID,
    };
  }
  if (removalType === personType.HOUSEHOLD) {
    return {
      signupListItemID,
      householdID,
    };
  }
  if (removalType === personType.ADDITIONAL_NAME) {
    return {
      signupListItemID,
      additionalNameID,
    };
  }
};

export default function Modal({
  user,
  household,
  additionalName,
  signupItemID,
  showDeletePaidSignupModal,
  setShowDeletePaidSignupModal,
  removalType,
  setRemovalType,
  updatedNumberOfSlots,
}) {
  const [state] = useSignups();
  const [data, setData] = React.useState();
  const [error, setError] = React.useState();

  const { isItemType } = useGetSignupListType();

  const { currentSignupList } = state;

  const currentSignupItem = currentSignupList?.signupItems.find(
    (item) => item.ID === signupItemID
  );
  const isSuggestedDonation = currentSignupList?.fundIt?.isSuggestedDonation;

  const displayName = getDisplayName({ user, household, additionalName });

  useEffect(
    function getRemovalAndRefundData() {
      if (showDeletePaidSignupModal) {
        let postData = getPostDataForRefundInfo({
          removalType,
          signupListItemID: signupItemID,
          userID: user?.userID,
          householdID: household?.householdID,
          additionalNameID: additionalName?.additionalNameID,
          isItemType,
          updatedNumberOfSlots,
        });

        let start = performance.now();
        API.getRefundInfo(postData)
          .post()
          .then((res) => {
            // console.log("getRefundInfo res", res);
            let end = performance.now();
            if (end - start > 500) {
              setData(res);
            } else {
              setTimeout(() => {
                setData(res);
              }, 500 - (end - start));
            }
          })
          .catch((err) => {
            setError("get_refund_info_error");
          });
      }
    },
    [
      showDeletePaidSignupModal,
      additionalName,
      household,
      signupItemID,
      user,
      removalType,
      isItemType,
      updatedNumberOfSlots,
    ]
  );

  const close = () => {
    setError(undefined);
    setData(undefined);
    setShowDeletePaidSignupModal(false);
    setRemovalType("");
  };

  return (
    <FnrModal show={showDeletePaidSignupModal}>
      <ErrorBoundary FallbackComponent={<div />}>
        <ContentContainer>
          <CloseButton onClick={close}>
            <i className="fas fa-times-circle" />
          </CloseButton>
          {error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : !data ? (
            <Loading />
          ) : (
            <RemovalContent
              usersToRemove={data?.respondentsToRemove}
              displayName={displayName}
              refundAmount={data?.refundAmount}
              currentSignupItem={currentSignupItem}
              closeModal={close}
              removalType={removalType}
              user={user}
              household={household}
              additionalName={additionalName}
              signupListID={currentSignupList.ID}
              setError={setError}
              updatedNumberOfSlots={updatedNumberOfSlots}
              isSuggestedDonation={isSuggestedDonation}
            />
          )}
        </ContentContainer>
      </ErrorBoundary>
    </FnrModal>
  );
}

const ContentContainer = styled.div`
  position: relative;
  border-radius: 16px;
  background: white;
  box-shadow: 1px 1px 3px rgb(0 0 0 / 10%);
  font-weight: 400;
`;
const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 2px;
  border-radius: 50%;
  font-size: 22px;
  z-index: 10;
  color: #c3c3c3;
  background: none;
  border: none;
  transition: all 0.2s ease;

  &:hover {
    cursor: pointer;
    scale: 1.1;
    color: #278dc1;
  }
`;

const RemovalContent = ({
  usersToRemove,
  displayName,
  refundAmount,
  currentSignupItem,
  closeModal,
  removalType,
  user,
  household,
  additionalName,
  signupListID,
  setError,
  updatedNumberOfSlots,
  isSuggestedDonation,
}) => {
  if (refundAmount > 0 && !isSuggestedDonation) {
    return (
      <RemoveWithRefund
        usersToRemove={usersToRemove}
        displayName={displayName}
        refundAmount={refundAmount}
        currentSignupItem={currentSignupItem}
        closeModal={closeModal}
        removalType={removalType}
        user={user}
        household={household}
        additionalName={additionalName}
        signupListID={signupListID}
        setError={setError}
        updatedNumberOfSlots={updatedNumberOfSlots}
      />
    );
  } else {
    return (
      <NoRefund
        usersToRemove={usersToRemove}
        displayName={displayName}
        currentSignupItem={currentSignupItem}
        closeModal={closeModal}
        removalType={removalType}
        user={user}
        household={household}
        additionalName={additionalName}
        signupListID={signupListID}
        setError={setError}
        updatedNumberOfSlots={updatedNumberOfSlots}
      />
    );
  }
};
